import { useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/apiService';
import { ALL_COMPETITIONS } from '../../helpers/CompetitionFilter';


// Each row carries its figures as a name/value list rather than fixed keys.
const valueOf = (row, name) => row?.data?.find((entry) => entry?.name === name)?.value ?? null;

// "24%" -> 24. Anything unparseable reads as no figure rather than 0%.
const percentOf = (value) => {
    const parsed = Number.parseFloat(String(value ?? '').replace('%', ''));
    return Number.isFinite(parsed) ? parsed : null;
};

// The feed has no "all leagues" row, so the All chip pools every league's figure,
// weighted by how many games each one is graded on. Leagues report different
// timeframes ("2026/27" vs "2026"), hence the generic label.
const combineRows = (rows) => {
    const scored = rows
        .map((row) => ({ percent: percentOf(valueOf(row, 'Correct')), matches: row?.match_count ?? 0 }))
        .filter((entry) => entry.percent !== null && entry.matches > 0);

    const matches = scored.reduce((sum, entry) => sum + entry.matches, 0);
    if (matches === 0) return null;

    const percent = scored.reduce((sum, entry) => sum + entry.percent * entry.matches, 0) / matches;

    return {
        timeframe: 'Season to date - All leagues',
        market: rows[0]?.market,
        match_count: matches,
        data: [{ name: 'Correct', value: `${Math.round(percent * 10) / 10}%` }],
    };
};

const Skeleton = ({ hideScore }) => (
    <div className="d-flex flex-column gap-2" aria-hidden="true">
        <div className="d-flex align-items-center justify-content-between gap-2">
            <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 150, height: 18 }} />
            <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 52, height: 15 }} />
        </div>
        {!hideScore && (
            <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 78, height: 21 }} />
                <span className="shimmer bg-dark-mode-700 rounded-2 d-block" style={{ width: 46, height: 21 }} />
            </div>
        )}
    </div>
);

// `rows` is the whole accuracy feed (every league), owned by TrackRecord; null while
// it's still loading. `hideScore` drops the market / % line (the public homepage
// shows only the season and games count; the % is for beta users on the dashboard).
const PredictionAccuracyCard = ({ rows, competitionId, className = '', hideScore = false }) => {
    // No league picked yet, or the feed hasn't landed — both are still loading.
    const loading = competitionId === null || competitionId === undefined || rows === null;

    const row = loading ? null
        : competitionId === ALL_COMPETITIONS ? combineRows(rows)
            : rows.find((entry) => String(entry?.competition_id) === String(competitionId)) ?? null;

    const correct = row ? valueOf(row, 'Correct') : null;
    const matches = row?.match_count ?? 0;

    // A league with no settled matches has no accuracy to report — "0 games, 0%" reads
    // as a bad result rather than an empty sample, so drop the card instead.
    if (!loading && (!row || !correct || matches === 0)) return null;

    return (
        <div className={`sk-result-card w-100 rounded-5 p-md-4 p-3 d-flex flex-column gap-2 ${className}`.trim()}>
            {loading ? (
                <Skeleton hideScore={hideScore} />
            ) : (
                <>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <p className="extra-small fw-semi-bold text-white mb-0">{row.timeframe}</p>
                        <span className="extra-extra-small fw-semi-bold text-sk-green text-nowrap py-1">
                            {matches} {matches === 1 ? 'game' : 'games'}
                        </span>
                    </div>

                    {!hideScore && (
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <p className="small text-white-80 mb-0">{row.market}</p>
                            <p className="small fw-semi-bold text-white mb-0">{correct}</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// Dashboard version: fetches the feed itself and shows every league combined.
export const AllLeaguesAccuracyCard = ({ className = '' }) => {
    const [rows, setRows] = useState(null);
    const requested = useRef(false);

    useEffect(() => {
        if (requested.current) return;
        requested.current = true;

        apiService
            .apiGet(`${import.meta.env.VITE_API_URL}/public/benchmarks/accuracy?market=exact_score`)
            .then(
                (response) => setRows(Array.isArray(response?.data) ? response.data : []),
                () => setRows([])
            );
    }, []);

    return <PredictionAccuracyCard rows={rows} competitionId={ALL_COMPETITIONS} className={className} />;
};

export default PredictionAccuracyCard;
